import {VenueContact} from '../../api/venue/types'

export type ContactLink = {href: string; label: string} | null

const BLOCKED_DIGITS = ['948951432', '2032901106']

const digitsOf = (value: string) => value.replace(/\D/g, '')
const isUrl = (value: string) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value) || /^(www\.|[^\s/@]+\.[a-z]{2,}\/)/i.test(value)
const withProtocol = (value: string) => (/^https?:\/\//i.test(value) ? value : `https://${value.replace(/^\/+/, '')}`)
const handleOf = (value: string) => value.trim().replace(/^@/, '')

const normType = (type: unknown) => String(type ?? '').toUpperCase()

const isBlocked = (value: string) => {
  const digits = digitsOf(value)
  return BLOCKED_DIGITS.some((blocked) => digits.includes(blocked))
}

const socialLink = (value: string, base: string) =>
  isUrl(value) ? withProtocol(value) : `${base}${handleOf(value)}`

/** Returns a tappable link for a contact, or null when it can only be shown as text. */
export const buildContactLink = ({type, value}: Pick<VenueContact, 'type' | 'value'>): ContactLink => {
  const v = ((value as string | undefined) ?? '').trim()
  if (!v) {
 return null
}
  switch (normType(type)) {
    case 'PHONE': {
      const num = v.replace(/[^\d+]/g, '')
      return num ? {href: `tel:${num}`, label: v} : null
    }
    case 'EMAIL':
      return {href: `mailto:${v}`, label: v}
    case 'WHATSAPP': {
      if (isUrl(v)) {
 return {href: withProtocol(v), label: v}
}
      const digits = digitsOf(v)
      return digits.length >= 8 ? {href: `https://wa.me/${digits}`, label: v} : null
    }
    case 'TELEGRAM': {
      if (isUrl(v)) {
 return {href: withProtocol(v), label: v}
}
      const handle = handleOf(v)
      return /^[A-Za-z0-9_]{5,}$/.test(handle) ? {href: `https://t.me/${handle}`, label: v} : null
    }
    case 'LINE': {
      if (isUrl(v)) {
        return {href: withProtocol(v), label: v}
      }
      return {href: `https://line.me/R/ti/p/${encodeURIComponent(v)}`, label: v}
    }
    case 'MESSENGER':
      return {href: socialLink(v, 'https://m.me/'), label: v}
    case 'FACEBOOK':
      return {href: socialLink(v, 'https://facebook.com/'), label: v}
    case 'INSTAGRAM':
      return {href: socialLink(v, 'https://instagram.com/'), label: v}
    case 'TIKTOK':
      return {href: socialLink(v, 'https://tiktok.com/@'), label: v}
    case 'WEBSITE':
      return {href: withProtocol(v), label: v}
    default:
      return null
  }
}

export type VenueChannel = {type: string; text: string; value: string; href: string | null; label: string}

type ChannelSource = {contacts?: VenueContact[]; phone?: string; whatsapp?: string; email?: string; website?: string}

// LINE has no text parameter, so only WhatsApp links carry the prefilled message
const withPrefill = (type: string, href: string | null, text?: string) =>
  href && text && type === 'WHATSAPP' && /^https:\/\/wa\.me\/\d+$/.test(href) ? `${href}?text=${encodeURIComponent(text)}` : href

const CHANNEL_ORDER = ['PHONE', 'WHATSAPP', 'LINE', 'MESSENGER', 'TELEGRAM', 'INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'WEBSITE', 'EMAIL']

/** Venue channels: not a blocked number, de-duplicated, stable order. Unlinkable or unknown types stay as plain rows. */
export const venueChannels = (venue?: ChannelSource | null, options?: {prefillText?: string}): VenueChannel[] => {
  if (!venue) {
    return []
  }
  const all: VenueContact[] = [
    ...(venue.contacts ?? []),
    ...(['phone', 'whatsapp', 'email', 'website'] as const)
      .filter((key) => venue[key])
      .map((key) => ({type: key, text: '', value: venue[key] ?? ''})),
  ]
  const seen = new Set<string>()
  const channels: VenueChannel[] = []
  all.forEach((contact) => {
    const value = ((contact.value as string | undefined) ?? '').trim()
    if (!value || isBlocked(value)) {
      return
    }
    const link = buildContactLink(contact)
    const type = normType(contact.type)
    const key = `${type}|${(link?.href ?? value).toLowerCase()}`
    if (seen.has(key)) {
      return
    }
    seen.add(key)
    const href = link?.href ?? null
    channels.push({type, text: contact.text, value, href: withPrefill(type, href, options?.prefillText), label: link?.label ?? value})
  })
  const rank = (type: string) => (CHANNEL_ORDER.includes(type) ? CHANNEL_ORDER.indexOf(type) : CHANNEL_ORDER.length)
  return channels.sort((a, b) => rank(a.type) - rank(b.type))
}


const stripUrl = (value: string) =>
  value
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '')

const HANDLE_TYPES = ['INSTAGRAM', 'TIKTOK', 'FACEBOOK', 'TELEGRAM', 'MESSENGER']

const socialHandle = (value: string) => {
  const v = value.trim()
  if (!isUrl(v)) {
    const handle = handleOf(v)
    return /^[\w.]+$/.test(handle) ? `@${handle}` : null
  }
  const segment = stripUrl(v).split(/[?#]/)[0].split('/')[1]?.replace(/^@/, '')
  return segment && /^[\w.]+$/.test(segment) && !/\.(php|html?)$/i.test(segment) ? `@${segment}` : null
}

/** Short value for the sub line: links lose protocol, www and trailing slash; socials show the handle when it can be derived. */
export const shortChannelValue = ({type, value}: Pick<VenueChannel, 'type' | 'value'>) => {
  const v = value.trim()
  if (HANDLE_TYPES.includes(type)) {
    return socialHandle(v) ?? stripUrl(v)
  }
  return type === 'PHONE' || type === 'EMAIL' || !isUrl(v) ? v : stripUrl(v)
}

/** Main line: the venue's own text when it adds something, else the platform name. Sub line: the short value. */
export const channelTexts = (channel: Pick<VenueChannel, 'text' | 'type' | 'value'>, platformName: string) => {
  const subtitle = shortChannelValue(channel)
  const text = (channel.text || '').trim()
  const same = [channel.value.trim(), subtitle].some((v) => v.toLowerCase() === text.toLowerCase())
  return {title: text && !same ? text : platformName, subtitle}
}

export const platformNameKey = (type: string) => `channel_${type.toLowerCase()}`
